import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBarChart2, FiCalendar, FiDollarSign, FiSave, FiStar, FiUsers, FiTag } = FiIcons;
const HOST_ID = 'aox-sponsor-intelligence-host';
const PLACEMENTS = [['home','Αρχική'],['matches','Αγώνες'],['team','Ομάδα'],['academy','Ακαδημία'],['media','Media']];
const TEAMS = ['Senior','K17','K14','K12','K10','K6'];
const money = (value) => new Intl.NumberFormat('el-GR',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(Number(value||0));

const Panel = () => {
  const [rows,setRows] = useState([]);
  const [categories,setCategories] = useState([]);
  const [loading,setLoading] = useState(true);
  const [savingId,setSavingId] = useState(null);
  const [message,setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setMessage('');
    const [{ data: sponsorRows, error: sponsorError }, { data: categoryRows, error: categoryError }] = await Promise.all([
      supabase.from('sponsors_aox1234').select('*').order('sort_order',{ascending:true}),
      supabase.from('sponsor_categories_aox1234').select('*').order('sort_order',{ascending:true}).order('name',{ascending:true}),
    ]);
    if (sponsorError || categoryError) {
      setMessage(`Σφάλμα: ${(sponsorError || categoryError).message}`);
    }
    setRows(sponsorRows || []);
    setCategories(categoryRows || []);
    setLoading(false);
  };
  useEffect(()=>{ load(); },[]);

  const stats = useMemo(()=>{
    const now = new Date();
    const in30 = new Date(now); in30.setDate(in30.getDate()+30);
    const active = rows.filter(r=>r.is_active!==false);
    return {
      active: active.length,
      value: active.reduce((sum,r)=>sum+Number(r.amount||0),0),
      featured: active.filter(r=>r.featured).length,
      expiring: active.filter(r=>r.end_date && new Date(`${r.end_date}T12:00:00`)>=now && new Date(`${r.end_date}T12:00:00`)<=in30).length,
    };
  },[rows]);

  const patch=(id,values)=>setRows(prev=>prev.map(row=>row.id===id?{...row,...values}:row));
  const toggleArray=(id,field,value)=>{
    const row=rows.find(item=>item.id===id);
    const current=row?.[field]||[];
    patch(id,{[field]:current.includes(value)?current.filter(x=>x!==value):[...current,value]});
  };

  const save=async(row)=>{
    setSavingId(row.id); setMessage('');
    const { error } = await supabase.from('sponsors_aox1234').update({
      type: row.type || null,
      is_active:row.is_active!==false,
      season_year:row.season_year||null,
      amount:row.amount===''||row.amount==null?null:Number(row.amount),
      start_date:row.start_date||null,
      end_date:row.end_date||null,
      placements:row.placements||[],
      team_categories:row.team_categories||[],
      tagline:row.tagline||null,
      featured:Boolean(row.featured),
      admin_notes:row.admin_notes||null,
      updated_at:new Date().toISOString(),
    }).eq('id',row.id);
    setMessage(error?`Σφάλμα: ${error.message}`:`Αποθηκεύτηκε: ${row.name}`);
    setSavingId(null);
  };

  const categoryOptionsFor = (row) => {
    const active = categories.filter(c => c.is_active !== false);
    const current = categories.find(c => c.slug === row.type);
    if (current && !active.some(c => c.id === current.id)) return [current, ...active];
    return active;
  };

  return <section className="mb-8 rounded-3xl border border-slate-200 bg-slate-950 p-5 text-white shadow-xl md:p-7">
    <div className="flex flex-wrap items-center justify-between gap-4"><div><div className="text-xs font-black uppercase tracking-[0.18em] text-blue-300">Sponsor Intelligence</div><h2 className="mt-1 text-2xl font-black">Έλεγχος αξίας & προβολής χορηγιών</h2></div><SafeIcon icon={FiBarChart2} className="h-8 w-8 text-blue-300" /></div>
    <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">{[[FiUsers,'Ενεργοί',stats.active],[FiDollarSign,'Συνολική αξία',money(stats.value)],[FiStar,'Featured',stats.featured],[FiCalendar,'Λήγουν ≤30 ημέρες',stats.expiring]].map(([icon,label,value])=><div key={label} className="rounded-2xl border border-white/10 bg-white/5 p-4"><SafeIcon icon={icon} className="h-5 w-5 text-blue-300"/><div className="mt-3 text-2xl font-black">{value}</div><div className="text-xs text-slate-400">{label}</div></div>)}</div>
    {message&&<div className="mt-4 rounded-xl bg-white/10 px-4 py-3 text-sm text-blue-100">{message}</div>}
    {loading?<div className="py-8 text-center text-slate-400">Φόρτωση...</div>:<div className="mt-6 space-y-4">{rows.map(row=><div key={row.id} className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 md:p-5">
      <div className="mb-5 flex justify-center">
        <div
          className="relative h-32 w-full max-w-sm overflow-hidden rounded-2xl border border-white/10 bg-white shadow-sm"
          style={row.image_url ? {
            backgroundImage: `url("${row.image_url}")`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          } : undefined}
          role="img"
          aria-label={`${row.name} logo`}
        >
          {!row.image_url && <div className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm font-black text-slate-500">{row.name}</div>}
        </div>
      </div>
      <div className="grid gap-4 xl:grid-cols-[1.25fr_.85fr_.65fr_.65fr_.65fr_auto] xl:items-end">
        <div><div className="text-center font-black xl:text-left">{row.name}</div><input value={row.tagline||''} onChange={e=>patch(row.id,{tagline:e.target.value})} placeholder="Σύντομο μήνυμα / tagline" className="mt-2 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none"/></div>
        <label className="text-xs text-slate-300"><span className="inline-flex items-center"><SafeIcon icon={FiTag} className="mr-1 h-3.5 w-3.5"/>Κατηγορία</span><select value={row.type||''} onChange={e=>patch(row.id,{type:e.target.value})} className="mt-1 w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white"><option value="">Χωρίς κατηγορία</option>{categoryOptionsFor(row).map(category=><option key={category.id} value={category.slug}>{category.name}{category.is_active===false?' (ανενεργή)':''}</option>)}</select></label>
        <label className="text-xs text-slate-300">Σεζόν<input value={row.season_year||''} onChange={e=>patch(row.id,{season_year:e.target.value})} className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm"/></label>
        <label className="text-xs text-slate-300">Αξία €<input type="number" min="0" value={row.amount??''} onChange={e=>patch(row.id,{amount:e.target.value})} className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm"/></label>
        <label className="text-xs text-slate-300">Λήξη<input type="date" value={row.end_date||''} onChange={e=>patch(row.id,{end_date:e.target.value})} className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm"/></label>
        <button onClick={()=>save(row)} disabled={savingId===row.id} className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 font-black hover:bg-blue-500 disabled:opacity-50"><SafeIcon icon={FiSave} className="mr-2 h-4 w-4"/>Save</button>
      </div>
      <div className="mt-4 flex flex-wrap justify-center gap-2 xl:justify-start"><button onClick={()=>patch(row.id,{is_active:row.is_active===false})} className={`rounded-full px-3 py-1.5 text-xs font-black ${row.is_active!==false?'bg-emerald-500/20 text-emerald-200':'bg-red-500/20 text-red-200'}`}>{row.is_active!==false?'Ενεργός':'Ανενεργός'}</button><button onClick={()=>patch(row.id,{featured:!row.featured})} className={`rounded-full px-3 py-1.5 text-xs font-black ${row.featured?'bg-yellow-300 text-yellow-950':'bg-white/10 text-slate-200'}`}>Featured</button>{PLACEMENTS.map(([value,label])=><button key={value} onClick={()=>toggleArray(row.id,'placements',value)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${(row.placements||[]).includes(value)?'bg-blue-500 text-white':'bg-white/10 text-slate-300'}`}>{label}</button>)}</div>
      <div className="mt-3 flex flex-wrap justify-center gap-2 xl:justify-start">{TEAMS.map(team=><button key={team} onClick={()=>toggleArray(row.id,'team_categories',team)} className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${(row.team_categories||[]).includes(team)?'bg-purple-500/30 text-purple-100':'bg-white/5 text-slate-400'}`}>{team}</button>)}</div>
    </div>)}</div>}
  </section>;
};

const SponsorIntelligencePortal=()=>{
  const location=useLocation();
  const [node,setNode]=useState(null);

  useEffect(()=>{
    const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
    const onSponsorsPage = normalizedPath === '/admin/sponsors' || normalizedPath.startsWith('/admin/sponsors/');

    if(!onSponsorsPage){
      setNode(null);
      return undefined;
    }

    let cancelled=false;
    let observer;

    const findHost=()=>{
      if(cancelled) return false;
      const target=document.getElementById(HOST_ID);
      if(target){
        setNode(target);
        return true;
      }
      return false;
    };

    if(!findHost()){
      observer=new MutationObserver(()=>{
        if(findHost() && observer) observer.disconnect();
      });
      observer.observe(document.body,{childList:true,subtree:true});
    }

    return()=>{
      cancelled=true;
      if(observer) observer.disconnect();
      setNode(null);
    };
  },[location.pathname]);

  return node?createPortal(<Panel/>,node):null;
};
export default SponsorIntelligencePortal;
